import type { CartData } from "@/api/cart/types.d";
import { pxtorem } from "@/utils/pxtorem";
import useNavigation from "@/utils/useNavigate";
import { CartGroupComp } from "@akc/mx-taro-h5-components";
import React, { ReactNode, useMemo } from "react";
import Empty from "../components/Empty";
import ProductList from "../components/ProductList";
import styles from "../index.module.less";

/**
 * 商品列表渲染Hook
 * 根据购物车数据和状态生成对应的商品列表JSX
 */
export const useProductList = (
  loading: boolean,
  cartData: CartData | null,
  cartShareNo: string | null,
  goodsCount: number,
  handleItemSelect: (groupIndex: number, itemIndex: number) => void,
  handleGroupSelect: (groupIndex: number, selected: boolean) => void,
  trackBtnClick: (button_name: string, extra?: Record<string, any>) => void,
): ReactNode => {
  const { navigate2H5, navigate2Product, navigate2BrandDetail } = useNavigation();

  // 返回商品列表JSX
  return useMemo(() => {
    // 第一次加载时显示空态组件
    if (loading && !cartData) {
      return <Empty text="加载中..." />;
    }

    if (!cartShareNo || !cartData || goodsCount === 0) {
      return <Empty text="暂无数据" />;
    }

    if (goodsCount > 0 && !cartData.effectiveCommodityList.length) {
      return (
        <div>
          <Empty text="商品已全部售罄/下架，看看其他好物吧～" />
          <ProductList />
        </div>
      );
    }

    return (
      <div className={styles.cartList}>
        {/* 有效可操作商品列表 */}
        {cartData.effectiveCommodityList.map((item, index) => (
          <CartGroupComp
            key={`${item.commodityId}-${index}`}
            groupInfo={item}
            scene="share"
            style={{
              marginTop: pxtorem(8),
              marginLeft: pxtorem(12),
              marginRight: pxtorem(12),
              borderRadius: pxtorem(10),
            }}
            onSelectChangeAction={(selected, item, itemIndex) => {
              handleItemSelect(index, itemIndex);
            }}
            onSelectGroupChangeAction={(selectGroup, isSelected) => {
              handleGroupSelect(index, isSelected);
            }}
            onGroupTopAction={selectGroup => {
              trackBtnClick("分组标题");
              // groupType 分组类型 0:档期 1:常销 2:NX营销. 4:店铺
              if (selectGroup.groupType === 0) {
                // 按档期分组，跳档期详情
                navigate2H5(`/mx-shop-micro/activity/${selectGroup.activityNo}?activityNo=${selectGroup.activityNo}`);
                return;
              }
              if (selectGroup.groupType === 1) {
                // 按常销分组，跳品牌详情
                navigate2BrandDetail(selectGroup.brandNo);
                return;
              }
              if (selectGroup.groupType === 2) {
                console.log("NX分组不跳转");
                return;
              }
              // 4店铺分组，跳转到店铺首页
              navigate2H5(`/mx-shop-micro/decorate?merchantShopCode=${selectGroup.merchantShopCode}`);
            }}
            onGroupCardAction={(selectGroup, item) => {
              trackBtnClick("商品卡片", { skuId: item.externalSkuCode });
              navigate2Product({
                activitySpuId: item.spuId,
                activityId: item.activityId ? item.activityId : undefined,
                selectSkuId: item.selectSkuId ? item.selectSkuId : undefined,
              });
            }}
          />
        ))}

        {/* 失效商品 */}
        {!!cartData?.invalidCommodityList?.length && (
          <CartGroupComp
            scene="share"
            isInvalid
            style={{
              marginTop: pxtorem(8),
              marginLeft: pxtorem(12),
              marginRight: pxtorem(12),
              borderRadius: pxtorem(10),
            }}
            groupInfo={{ cartLines: cartData.invalidCommodityList }}
          />
        )}
      </div>
    );
  }, [loading, cartShareNo, cartData, goodsCount, handleItemSelect, handleGroupSelect, navigate2H5, navigate2Product]);
};
